import { useCallback } from 'react';
import { getCapacityUnit, getThroughputUnit } from 'utils/configUtilities';
import { useConfigInputs, useConfigActions, useMetrics, useMetricsOverlay, usePricing, useCalcStatus, useVendorCtx } from 'shared/state/configContexts';
import { useAbility, PERM } from 'shared/access';
import { formatCapacityPb, capacityPbParts } from 'shared/lib/format';
import type { RailCtx } from 'shared/state/railTypes';
import type { DonutTone } from './Donut';

type Medium = { id: string; tone: DonutTone; raw: number };

/** One set's ring, from display-unit figures (TB, or TiB in binary mode). The coloured
 *  arcs together are useable ÷ raw; that useable is shared out between the media by
 *  their share of the set's raw capacity, because the engine reports useable per set,
 *  not per medium. */
function ring(isPBUnit: boolean, raw: number, useable: number, scheme: unknown, media: Medium[]) {
  const efficiency = raw > 0 ? useable / raw : 0;
  // The ring prints the unit on its own line under the figure, hence the parts.
  const { figure, unit } = capacityPbParts(raw, isPBUnit);
  return {
    raw: figure,
    unit,
    useable: formatCapacityPb(useable, isPBUnit),
    detail: `${scheme} · ${(efficiency * 100).toFixed(0)}%`,
    arcs: media.map((m) => ({ id: m.id, tone: m.tone, fraction: raw > 0 ? (m.raw / raw) * efficiency : 0 })),
  };
}

/** Derives every rail value from config/metrics/pricing on each render — nothing is
 *  mirrored into state. The only write anywhere in this feature is the PB/PiB toggle. */
export function useLiveRail() {
  const { config } = useConfigInputs();
  const { updateConfig } = useConfigActions();
  const metrics = useMetrics();
  const { baselineMetrics, affectedKeys } = useMetricsOverlay();
  const { pricingData } = usePricing();
  const { isCalculating } = useCalcStatus();
  const { vendor } = useVendorCtx();
  const ability = useAbility();

  const isPBUnit = Boolean(config.isPBUnit);
  const ctx: RailCtx = {
    metrics,
    config,
    unit: getCapacityUnit(isPBUnit),
    throughputUnit: getThroughputUnit(isPBUnit),
  };

  // CONF-212: the same tile evaluated against the REAL metrics gives its
  // Current figure; only used for tiles whose keys an override moved.
  const baselineCtx: RailCtx = { ...ctx, metrics: baselineMetrics };
  const isAffected = (keys: string[] | undefined) => (keys ?? []).some((k) => affectedKeys.has(k));

  const num = (key: string) => Number(metrics?.[key] ?? 0);
  const softwareOnly = vendor.businessRules.softwareSubscriptionOnly === true;
  const flashSetOn = Boolean(config.afaEnabled);

  return {
    ctx,
    baselineCtx,
    isAffected,
    isCalculating,
    isPBUnit,
    // Switch emits the checked value it is about to become; LiveRail passes
    // checked={!isPBUnit}, so `next` is the inverse of the new isPBUnit.
    onUnitToggle: useCallback((next: boolean) => updateConfig({ isPBUnit: !next }), [updateConfig]),
    tally: {
      directors: Number(config.veloCount ?? 0),
      flash: config.afaEnabled ? Number(config.afaNodeCount ?? 0) : 0,
      mixed: config.hybridEnabled ? Number(config.hybridNodeCount ?? 0) : 0,
    },
    // One ring per storage set, as in the CONF-140 prototype. A set that is off keeps
    // its place, dimmed, so the block does not reflow when a set is toggled.
    donuts: [
      {
        id: 'flash', name: 'Flash Set', tone: 'flash' as const, off: !flashSetOn,
        ...ring(isPBUnit, num('totalAfaRawCapacity'), num('afaEffectiveCapacity'), config.afaEncodingScheme, [
          { id: 'flash', tone: 'flash', raw: num('totalAfaRawCapacity') },
        ]),
      },
      {
        id: 'capacity', name: 'Capacity Set', tone: 'brand' as const, off: !config.hybridEnabled,
        ...ring(isPBUnit, num('totalHybridRawCapacity'), num('hybridEffectiveCapacity'), config.hybridEncodingScheme, [
          { id: 'disk', tone: 'brand', raw: num('totalHybridRawCapacity') - num('totalHybridSsdCapacity') },
          { id: 'flash', tone: 'flash', raw: num('totalHybridSsdCapacity') },
        ]),
      },
    ],
    showPrice: ability.can(PERM.PRICING_VIEW),
    price: {
      label: softwareOnly ? 'VDURA Pricing' : 'Solution Price',
      total: Number((softwareOnly ? pricingData?.solution?.softwareTotal : pricingData?.solution?.grandTotal) ?? 0),
      perRaw: num('dollarsPerRawTB'),
      perUseable: num('dollarsPerEffectiveTB'),
      perEffective: num('dollarsPerCompressedEffectiveTB'),
    },
  };
}
